import {FC, useContext, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Button, Drawer, Typography} from "@mui/material";
import {palette, mobileNavBarHeight} from "@/constants";
import {IconAdminPlus} from "@/icons/IconAdminPlus";
import {IconAdminCourses} from "@/icons/IconAdminCourses";
// import {IconAdminReports} from "../../icons/IconAdminReports";
import {IconAdminUsers} from "@/icons/IconAdminUsers";
import {IconPlayButton} from "@/icons/IconPlayButton";
import {IconAdminQuestion} from "@/icons/IconAdminQuestion";
import {IconSettings} from "@/icons/IconSettings";
import {Link, useLocation} from "react-router-dom";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import {PlaylistPlay} from "@mui/icons-material";
import useDevCycleSdk from "../../hooks/useDevCycleSdk";
import {AdminQuickCreateModal} from "@/pages/ManagerView/AdminQuickCreateModal";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AssessmentIcon from "@mui/icons-material/Assessment";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import bwIcon from "../../icons/bwicon.svg";

const useStyles = makeStyles()((theme) => ({
	sideMenu: {
		width: "283px",
		height: "100%",
		position: "fixed",
		"[class*=' MuiDrawer-paper']": {
			paddingTop: "0 !important"
		}
	},
	drawer: {
		width: "283px !important",
		height: "100%",
		overflow: "hidden",
		background: palette.black,
		transition: "all .3s ease-in-out",
		"& .MuiDrawer-paper": {
			position: "relative",
			height: "100vh",
			display: "flex",
			flexDirection: "column",
			backgroundColor: "#191919",
			borderRight: "1px solid rgba(255,255,255,0.1)",
			paddingTop: theme["toolbarHeight"],
			transition: "all .3s ease-in-out",
			width: "283px",
			overflowY: "auto",
			overflowX: "hidden",
			zIndex: theme.zIndex.appBar - 1,
			boxSizing: "border-box",
			[theme.breakpoints.down("lg")]: {
				height: `calc(100vh - ${mobileNavBarHeight})`,
				maxHeight: `calc(100vh - ${mobileNavBarHeight})`
			}
		}
	},
	header: {
		boxSizing: "border-box",
		padding: theme.spacing(3, 2),
		margin: theme.spacing(0, 3),
		borderBottom: "1px solid " + palette.grey10,
		flexShrink: 0
	},
	companyName: {
		fontSize: "20px",
		marginTop: "8px",
		marginBottom: "4px",
		letterSpacing: "0.15px"
	},
	sectionTitle: {
		color: palette.grey70,
		fontSize: "12px",
		marginBottom: theme.spacing(1),
		textTransform: "uppercase",
		letterSpacing: "2px"
	},
	sideMenuList: {
		"& a": {
			textDecoration: "none",
			color: theme.palette.common.white
		},
		padding: theme.spacing(1, 2),
		paddingBottom: "0px",
		margin: theme.spacing(4, 3),
		marginBottom: "0px",
		display: "flex",
		flexDirection: "column",
		gap: "0px",
		flex: "1 1 0%",
		minHeight: 0,
		boxSizing: "border-box",
		position: "relative"
	},
	contentWrapper: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(3),
		flex: "0 0 auto"
	},
	sideMenuSection: {
		display: "flex",
		flexDirection: "column",
		marginBottom: theme.spacing(1),
		gap: "4px"
	},
	generalSection: {
		display: "flex",
		flexDirection: "column",
		marginBottom: "0",
		marginTop: "auto",
		gap: "4px",
		paddingTop: theme.spacing(7.5),
		paddingBottom: "0px",
		flexShrink: 0
	},
	sideMenuLink: {
		cursor: "pointer",
		fontSize: "16px",
		letterSpacing: "0.15px",
		fontWeight: 400,
		display: "inline-flex",
		padding: "12px 0",
		gap: theme.spacing(4),
		alignItems: "center",
		transition: "0.35s",
		position: "relative",
		"&:after": {
			content: "''",
			opacity: "0",
			position: "absolute",
			left: theme.spacing(-2),
			bottom: "-3px",
			width: "0px",
			height: "4px",
			background: palette.orange,
			transition: "0.35s"
		},
		"& path": {
			fill: palette.grey60,
			transition: "0.35s"
		}
	},
	activeLink: {
		position: "relative",
		"&:after": {
			opacity: "1",
			width: "calc(100% + 32px)",
			transition: "0.35s"
		},
		"& path": {
			transition: "0.35s",
			fill: theme.palette.primary.main
		}
	},
	quickCreateSection: {
		display: "flex",
		justifyContent: "center",
		backgroundColor: `${theme.palette.primary.main}14`,
		textTransform: "uppercase",
		paddingTop: theme.spacing(0),
		paddingBottom: theme.spacing(0),
		height: "36px",
		color: theme.palette.primary.main,
		"svg": {
			color: theme.palette.primary.main,
			transition: theme.transitionSpeeds.fast,
			"path": {
				fill: "currentColor"
			}
		},
		"&:hover": {
			color: theme.palette.primary.contrastText,
			"& svg": {
				color: theme.palette.primary.contrastText
			}
		},
		border: `1px solid ${theme.palette.primary.main}`,
		borderRadius: "4px",
		marginLeft: theme.spacing(-2),
		marginRight: theme.spacing(-2)
	},
	iconAdminPlus: {
		width: "14px",
		height: "14px"
	},
	bwIconImage: {
		width: "22px",
		height: "22px",
		objectFit: "contain"
	}
}));

export const AdminSideMenuContent: FC = () => {
	const {cx, classes} = useStyles();
	const location = useLocation();
	const {t} = useSSTranslation();
	const [pageBaseContext] = useContext(PageBaseContext);
	const [quickCreateModalOpen, setQuickCreateModalOpen] = useState(false);
	const {playlists, monthEndReport, hideCreationButtons, contentCreationUpdates, teamProgress} = useDevCycleSdk();

	const isActive = (e: string): boolean => {
		return location.pathname.startsWith(e)
	}

	const linkElement = (link: string, name: string, icon: JSX.Element, dataTestId?: string, ariaLabel?: string): JSX.Element =>
		<Link to={link}>
			<Box
				className={cx(classes.sideMenuLink, isActive(link) ? classes.activeLink : undefined)}
				data-test-id={dataTestId}
				aria-label={ariaLabel}
			>{icon}{name}</Box>
		</Link>

	const externalLinkElement = (url: string, name: string, icon: string, dataTestId?: string, ariaLabel?: string): JSX.Element =>
		<Box
			className={classes.sideMenuLink}
			onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					window.open(url, "_blank", "noopener,noreferrer");
				}
			}}
			role="link"
			tabIndex={0}
			sx={{cursor: "pointer"}}
			data-test-id={dataTestId}
			aria-label={ariaLabel}
		>
			<img src={icon} alt="" className={classes.bwIconImage} />
			<span>{name}</span>
			<OpenInNewIcon sx={{fontSize: "22px"}} />
		</Box>

	return (
		<Box className={classes.sideMenu}>
			<Drawer
				className={classes.drawer}
				variant="permanent"
			>
				<Box className={classes.header}>
					<Typography className={classes.companyName}>{(pageBaseContext.organizationName) ?? ".."}</Typography>
					<Typography className={classes.sectionTitle}>Content Manager</Typography>
				</Box>
				<Box className={classes.sideMenuList}>
					<Box className={classes.contentWrapper}>
						{contentCreationUpdates && <Button className={classes.quickCreateSection} startIcon={<IconAdminPlus className={classes.iconAdminPlus} />} onClick={() => setQuickCreateModalOpen(true)} data-test-id="sidebar-create-button" aria-label="Open create menu">Create</Button>}
						{(!hideCreationButtons && !contentCreationUpdates) && <Box className={classes.sideMenuSection}>
							<Typography className={classes.sectionTitle}>CREATION</Typography>
							{linkElement("courses/create", "Create Course", <IconAdminPlus className={classes.iconAdminPlus} />, "sidebar-create-course", "Navigate to create course")}
						</Box>}
						<Box className={classes.sideMenuSection}>
							<Typography className={classes.sectionTitle}>MANAGEMENT</Typography>
							{teamProgress && linkElement("/admin/team-progress", t("team_progress_title", "Team Progress"), <QueryStatsIcon />, "sidebar-team-progress", "Navigate to Team Progress")}
							{linkElement("/admin/users", "Learner Progress", <IconAdminUsers />, "sidebar-learner-progress", "Navigate to Learner Progress")}
							{monthEndReport && linkElement("/admin/scheduled-reports", "Month-End Reports", <AssessmentIcon />, "sidebar-scheduled-reports", "Navigate to Month-End Reports")}
							{playlists && linkElement("/admin/playlists", "Playlists", <PlaylistPlay />, "sidebar-playlists", "Navigate to Playlists")}
							{linkElement("/admin/courses", "Courses", <IconAdminCourses />, "sidebar-courses", "Navigate to Courses")}
							{linkElement("/admin/quizzes", "Quizzes", <IconAdminQuestion />, "sidebar-quizzes", "Navigate to Quizzes")}
							{linkElement("/admin/lessons", "Lessons", <IconPlayButton />, "sidebar-lessons", "Navigate to Lessons")}
							{/* {announcements && linkElement("/admin/announcements", "Announcements", <IconAnnouncement />, "sidebar-announcements", "Navigate to Announcements")} */}
						</Box>
					</Box>
					<Box className={classes.generalSection}>
						<Typography className={classes.sectionTitle}>GENERAL</Typography>
						{externalLinkElement(process.env.REACT_APP_PROPELAUTH_ACCOUNT_SETTINGS_URI ?? "", "BW Account", bwIcon, "sidebar-bw-account", "Open BW Account in new tab")}
						{/* {linkElement("support", "Support", <IconAdminSupport />)} */}
						{linkElement("/admin/settings", "Content Settings", <IconSettings />, "sidebar-content-settings", "Navigate to Content Settings")}
					</Box>
					<Box sx={{
						height: "72px",
						minHeight: "88px",
						flexShrink: 0,
						"@media (max-width: 1199px)": {
							height: `calc(${mobileNavBarHeight} + 8px)`,
							minHeight: `calc(${mobileNavBarHeight} + 8px)`
						}
					}} />
				</Box>
			</Drawer>
			<AdminQuickCreateModal onClose={() => setQuickCreateModalOpen(false)} open={quickCreateModalOpen} />
		</Box>
	);
}
